import { useState, useEffect, useCallback } from 'react';
import { 
  CreditCard, TrendingUp, Users, ArrowUpRight, ArrowDownRight, Loader2, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import MainLayout from '@/components/layout/MainLayout';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const formatNumber = (num) => new Intl.NumberFormat('es-ES').format(num || 0);
const formatCurrency = (num) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(num || 0);
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const CHART_COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'];

const PLAN_COLORS = {
  free: 'bg-gray-500/20 text-gray-400',
  creador: 'bg-brand/20 text-brand',
  agencia: 'bg-purple-500/20 text-purple-400'
};

const AdminSuscripciones = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [revenueData, setRevenueData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const statsRes = await api.get('/admin/subscriptions/stats');
      setMetrics(statsRes.data);
      
      // Transform data for charts
      const planDist = statsRes.data.plan_distribution || {};
      const chartData = Object.entries(planDist).map(([name, data]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: data.count || 0
      }));
      setRevenueData({ by_plan: chartData });
    } catch (err) {
      toast.error('Error cargando datos de suscripciones');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-brand" />
        </div>
      </MainLayout>
    );
  }

  const pieData = revenueData?.by_plan ? revenueData.by_plan : [];

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="admin-suscripciones-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-main flex items-center gap-3">
              <CreditCard className="w-7 h-7 text-brand" />
              Suscripciones
            </h1>
            <p className="text-text-muted mt-1">Métricas de revenue y suscripciones activas</p>
          </div>
          <Button onClick={fetchData} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface border border-border-subtle rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-muted">Founders</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">Especial</span>
            </div>
            <p className="text-2xl font-bold text-text-main">{formatNumber(metrics?.founders_count || 0)}</p>
            <p className="text-xs text-amber-400 mt-1">Usuarios Founder</p>
          </div>
          
          <div className="bg-surface border border-border-subtle rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-muted">Creador</span>
              <Users className="w-4 h-4 text-brand" />
            </div>
            <p className="text-2xl font-bold text-text-main">{formatNumber(metrics?.plan_distribution?.creador?.count || 0)}</p>
            <p className="text-xs text-brand mt-1">Plan Creador</p>
          </div>
          
          <div className="bg-surface border border-border-subtle rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-muted">Agencia</span>
              <Users className="w-4 h-4 text-purple-400" />
            </div>
            <p className="text-2xl font-bold text-text-main">{formatNumber(metrics?.plan_distribution?.agencia?.count || 0)}</p>
            <p className="text-xs text-purple-400 mt-1">Plan Agencia</p>
          </div>
          
          <div className="bg-surface border border-border-subtle rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-muted">Ingresos</span>
              <TrendingUp className="w-4 h-4 text-green-400" />
            </div>
            <p className="text-2xl font-bold text-text-main">{formatCurrency((metrics?.revenue?.total || 0) / 100)}</p>
            <p className="text-xs text-green-400 mt-1">{metrics?.revenue?.transaction_count || 0} transacciones</p>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue by Plan */}
          <div className="bg-surface border border-border-subtle rounded-xl p-6">
            <h3 className="text-sm font-medium text-text-main mb-4">Distribución por Plan</h3>
            {pieData.length > 0 ? (
              <div className="flex items-center gap-6">
                <div className="w-48 h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${entry.name}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => formatNumber(value)}
                        contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {pieData.map((entry, index) => (
                    <div key={entry.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                        />
                        <span className="text-sm text-text-main">{entry.name}</span>
                      </div>
                      <span className="text-sm font-medium text-text-main">{formatNumber(entry.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-center text-text-muted py-8">Sin datos</p>
            )}
          </div>

          {/* New Users Chart */}
          <div className="bg-surface border border-border-subtle rounded-xl p-6">
            <h3 className="text-sm font-medium text-text-main mb-4">Nuevos Usuarios (30 días)</h3>
            {revenueData?.daily?.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueData.daily}>
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: '#888', fontSize: 10 }}
                      tickFormatter={(v) => new Date(v).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
                    />
                    <YAxis tick={{ fill: '#888', fontSize: 10 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px' }}
                      labelFormatter={(v) => new Date(v).toLocaleDateString('es-ES')}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="new_users" 
                      stroke="#8b5cf6" 
                      strokeWidth={2}
                      dot={false}
                      name="Nuevos usuarios"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-center text-text-muted py-8">Sin datos</p>
            )}
          </div>
        </div>

        {/* Subscriptions Table */}
        <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border-subtle">
            <h3 className="font-medium text-text-main">Suscripciones Activas</h3>
          </div>
          <table className="w-full">
            <thead className="bg-app-bg/50">
              <tr>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Usuario</th>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Plan</th>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Desde</th>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Estado</th>
              </tr>
            </thead>
            <tbody>
              {metrics?.recent_subscriptions?.length > 0 ? (
                metrics.recent_subscriptions.map((sub, idx) => (
                  <tr key={sub.id || `sub-${idx}`} className="border-t border-border-subtle">
                    <td className="p-4">
                      <p className="text-sm text-text-main">{sub.email}</p>
                      <p className="text-xs text-text-muted">{sub.name || 'Sin nombre'}</p>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${PLAN_COLORS[sub.subscription_plan] || PLAN_COLORS.free}`}>
                        {sub.subscription_plan}
                      </span>
                      {sub.is_founder && (
                        <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                          Founder
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-text-muted">{formatDate(sub.subscription_started_at)}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        sub.subscription_status === 'active' ? 'bg-green-500/20 text-green-400' :
                        sub.subscription_status === 'past_due' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {sub.subscription_status || 'Activa'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-text-muted">
                    No hay suscripciones activas todavía
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminSuscripciones;
